// openInstallableModal — Phase 4 real implementation.
//
// Registered as a shared function so el.call('openInstallableModal', opts)
// resolves from InstallableRail.js, InstallableRailItem.js, canvas tiles,
// dashboard, admin, and any other surface that needs to trigger the
// MarketplaceInstallModal without owning a marketplace island.
//
// Replaces the Phase 3 stub (which only console.warn'd + set root.state._installableModal).
//
// Delegation strategy:
//   1. Try to find a marketplace host via the marketplaceHostOf walk
//      (works when called from inside or adjacent to a MarketplaceIsland).
//   2. Fall back to root.state when outside a marketplace island — the
//      InstallableModalStub mounted at the app root also listens to
//      root.state._installableModal so it can show a fallback modal.
//   3. When a marketplace host IS found, delegate directly to the full
//      MarketplaceInstallModal via host.update({ installModal: ... }).
//
// Args:
//   domain        – 'integration'|'library'|'mcp'|'plugin'|'template'
//   kind          – kind key string (= preselectedKind), e.g. 'github'
//   installed     – boolean (true = manage flow, false = install flow)
//   defaultScope  – { type: 'org'|'workspace'|'project', id: string }
//   catalogFilter – filter object (category, applicable_to_types, applicable_to_scopes)
//   onInstalled   – (instance) => void callback
//
// Contract is the same as marketplace's openInstallableModal — this module
// is the single shared registration point; the marketplace package contains
// the full logic.

// checkCatalogFilter — the ONE catalogue-filter check.
//
// Registered as a shared function so `el.call('checkCatalogFilter', item,
// filter)` resolves from openInstallableModal below (the moment the modal
// OPENS) and from the marketplace's submitInstallModal (the moment the user
// presses Install — a defensive second look, because the catalogue can change
// in between). It used to be two copies: this file's held English sentences,
// the marketplace's read `marketplace.install.*`, so a Georgian workspace saw a
// different language depending on WHICH of the two refused. It is one function
// now, and the wording is the bundle's alone
// (DESIGN-SHARED-AICONTEXTBAR-AND-INSTALLABLE-ENGLISH-COPY-1).
//
// The filter is an object with optional keys:
//   category             – string; item.category must match
//   applicable_to_types  – string[]; item._liveKind?.applicable_to_types must overlap
//   applicable_to_scopes – string[]; item._liveKind?.applicable_to_scopes must overlap
//
// Returns null when the item passes, or the message — resolved through
// polyglot on the calling element, in the session language — when it fails.
// Each refusal is its own written-out key; the values it names (a category
// slug, type and scope ids) fill placeholders, never a concatenation.
//
// FA204: nothing here reads a module-scope name — the element arrives as `this`.
export function checkCatalogFilter(item, catalogFilter) {
  if (!catalogFilter || !item) return null
  const el = this
  if (catalogFilter.category && item.category !== catalogFilter.category) {
    return el
      .call('polyglot', 'marketplace.install.wrongCategory')
      .replace('{category}', item.category || '')
      .replace('{required}', catalogFilter.category)
  }
  if (
    Array.isArray(catalogFilter.applicable_to_types) &&
    catalogFilter.applicable_to_types.length > 0
  ) {
    const itemTypes = item._liveKind?.applicable_to_types || []
    const overlap = catalogFilter.applicable_to_types.some((t) => itemTypes.includes(t))
    if (!overlap) {
      return el
        .call('polyglot', 'marketplace.install.wrongTypes')
        .replace('{types}', catalogFilter.applicable_to_types.join(', '))
    }
  }
  if (
    Array.isArray(catalogFilter.applicable_to_scopes) &&
    catalogFilter.applicable_to_scopes.length > 0
  ) {
    const itemScopes = item._liveKind?.applicable_to_scopes || []
    const overlap = catalogFilter.applicable_to_scopes.some((s) => itemScopes.includes(s))
    if (!overlap) {
      return el
        .call('polyglot', 'marketplace.install.wrongScopes')
        .replace('{scopes}', catalogFilter.applicable_to_scopes.join(', '))
    }
  }
  return null
}

// FA204 / frank trap — every module-scope helper/table below used to be
// referenced bare from `openInstallableModal`, then this file's only export.
// Unresolvable once frank.toJSON'd for platform delivery
// (project_frank_drops_module_local_helpers). All of them now live INSIDE
// openInstallableModal's own body — nested declarations inside the
// exported function's own source are never rewritten by frank and keep
// working via normal JS closures. The one piece of logic with a second
// caller, the catalogue-filter check, is NOT nested: it is the exported
// `checkCatalogFilter` above, reached through `el.call`.

// Domains whose `kind` is an opaque PER-TENANT INSTANCE id, never a slug
// that could appear in `host.catalog` (the marketplace's own browse
// catalogue of static + SDK-project-backed entries — github/slack/notion/
// templates/libraries/apps). `host.catalog` genuinely DOES carry entries
// for library/template/plugin/app (prefixed `lib.`/`tpl.`/`plg.` ids,
// matched by `_findItemByKind` above) — those domains keep the catalogue
// lookup. `mcp` is different: McpConnector rows are free-form name+url
// Mongo docs with no enum/catalogue at all (see
// shared/components/InstallableRail.js's `_deriveSelfKinds` — each
// installed connector self-derives its own tile, `kind: <connector's real
// id>`). Matching that opaque id against host.catalog can never succeed —
// this was tickets/workspace.md "installed-tile manage click resolves
// against the wrong catalog for every non-integration domain": every
// domain OTHER than integration hit the same miss in theory, but mcp is
// the first to actually reach a browser with real self-derived data.
//
// NOTE: this is intentionally a DIFFERENT (narrower) set than
// InstallableRail.js's own `_KIND_CATALOG_DOMAINS` — that constant answers
// "does this domain have a kinds-ENUMERATION endpoint to poll?" (only
// `integration` does, via `listIntegrationKinds`), a different question
// from "does host.catalog carry entries for this domain at all?" (it does,
// for library/template/plugin/app — those are browsable in the main
// marketplace grid even though InstallableRail has no live kinds feed for
// them yet). Reusing InstallableRail's set here would have wrongly routed
// template/library/plugin/app manage-clicks (see canvas/pages/dashboard.js
// `OpenInstallable`/`Details`) away from their real, working catalogue hit.
export function openInstallableModal(opts) {
  // Walk up to find a marketplace host element (same algorithm as
  // marketplaceHostOf in marketplace/functions/index.js, duplicated
  // here to avoid cross-package imports).
  function _findMarketplaceHost(el) {
    let cur = el
    let lastWithState = null
    while (cur) {
      if (cur._marketplaceHost && cur.state) return cur.state
      if (cur.state) lastWithState = cur
      if (!cur.parent) break
      cur = cur.parent
    }
    return lastWithState?.state || el?.state || null
  }

  // Resolve item from catalogue by kind slug.
  // Mirrors the slug-matching logic in marketplace/functions/index.js
  // so the lookup works regardless of which package this is called from.
  function _slugForId(id) {
    return String(id || '')
      .replace(/^(int|reg|plg|tpl|lib)\./, '')
      .replace(/-/g, '_')
  }

  function _findItemByKind(catalog, kind) {
    if (!Array.isArray(catalog) || !kind) return null
    return (
      catalog.find((i) => {
        const s = _slugForId(i.id)
        return (
          s === kind ||
          i.id === kind ||
          i.id === `int.${kind}` ||
          i.id === `plg.${kind}` ||
          i.id === `lib.${kind}` ||
          i.id === `tpl.${kind}`
        )
      }) || null
    )
  }

  // Domains whose `kind` is an opaque PER-TENANT INSTANCE id, never a slug
  // that could appear in `host.catalog` (the marketplace's own browse
  // catalogue of static + SDK-project-backed entries — github/slack/notion/
  // templates/libraries/apps). `host.catalog` genuinely DOES carry entries
  // for library/template/plugin/app (prefixed `lib.`/`tpl.`/`plg.` ids,
  // matched by `_findItemByKind` above) — those domains keep the catalogue
  // lookup. `mcp` is different: McpConnector rows are free-form name+url
  // Mongo docs with no enum/catalogue at all (see
  // shared/components/InstallableRail.js's `_deriveSelfKinds` — each
  // installed connector self-derives its own tile, `kind: <connector's real
  // id>`). Matching that opaque id against host.catalog can never succeed —
  // this was tickets/workspace.md "installed-tile manage click resolves
  // against the wrong catalog for every non-integration domain": every
  // domain OTHER than integration hit the same miss in theory, but mcp is
  // the first to actually reach a browser with real self-derived data.
  //
  // NOTE: this is intentionally a DIFFERENT (narrower) set than
  // InstallableRail.js's own `_KIND_CATALOG_DOMAINS` — that constant answers
  // "does this domain have a kinds-ENUMERATION endpoint to poll?" (only
  // `integration` does, via `listIntegrationKinds`), a different question
  // from "does host.catalog carry entries for this domain at all?" (it does,
  // for library/template/plugin/app — those are browsable in the main
  // marketplace grid even though InstallableRail has no live kinds feed for
  // them yet). Reusing InstallableRail's set here would have wrongly routed
  // template/library/plugin/app manage-clicks (see canvas/pages/dashboard.js
  // `OpenInstallable`/`Details`) away from their real, working catalogue hit.
  const _NO_CATALOG_DOMAINS = new Set(['mcp'])

  // Generic config-schema for domains in `_NO_CATALOG_DOMAINS` that also need
  // a real form to CREATE a brand new instance (the "Add" button — no `kind`
  // yet, nothing to resolve). There is no catalogue to browse, so the "item"
  // IS the form itself. Field set mirrors
  // canvas/components/MCPConnectorPickerOverlay.js 1:1 (url, transport,
  // authKind, authToken; `displayName` reuses the modal's existing built-in
  // DisplayNameField, gated on `allows_multiple`) so both entry points to the
  // same backend collect the identical shape
  // `_submitMcpDomain`/`saveMcpConnector` expect. Rendered by the marketplace
  // modal's existing generic `DynamicConfigFields` — no changes needed there.
  const _NO_CATALOG_ADD_SCHEMAS = {
    mcp: {
      allows_multiple: true,
      auth_model: 'none', // keep the modal's own generic secret field hidden — authToken below covers it
      config_schema: {
        fields: [
          {
            key: 'url',
            label: 'apps.mcp.field.url',
            required: true,
            placeholder: 'https://mcp.example.com'
          },
          {
            key: 'transport',
            label: 'apps.mcp.field.transport',
            required: false,
            placeholder: 'sse'
          },
          {
            key: 'authKind',
            label: 'apps.mcp.field.authKind',
            required: false,
            placeholder: 'none'
          },
          {
            key: 'authToken',
            label: 'apps.mcp.field.authToken',
            required: false,
            placeholder: ''
          }
        ]
      }
    }
  }

  const el = this

  const domain = opts?.domain || null
  const kind = opts?.kind || opts?.preselectedKind || null
  const installed = !!opts?.installed
  const defaultScope = opts?.defaultScope || null
  const catalogFilter = opts?.catalogFilter || null
  const onInstalled = typeof opts?.onInstalled === 'function' ? opts.onInstalled : null
  // Self-describing display info the CLICK SITE already has (InstallableRailItem
  // forwards its own tile state — see InstallableRail.js's self-derived kinds).
  // Only consumed below for domains in `_NO_CATALOG_DOMAINS`, where there is no
  // catalogue to enrich the item from.
  const selfDisplay = opts?.display || null

  // 1. Walk element tree to find a MarketplaceIsland host.
  let host = null
  try {
    host = _findMarketplaceHost(el)
  } catch {}

  // 2. Fall back to root.state when outside a marketplace island.
  if (!host) {
    try {
      const root = el.getRoot?.()
      if (root?.state) host = root.state
    } catch {}
  }

  if (!host) {
    console.warn('[openInstallableModal] Could not resolve host state. opts:', opts)
    return
  }

  const catalog = host.catalog || []

  // 3. Resolve the item — per domain, against the source that actually owns
  // it (tickets/workspace.md "installed-tile manage click resolves against
  // the wrong catalog for every non-integration domain"). Domains in
  // `_NO_CATALOG_DOMAINS` (today: mcp) have no catalogue at all — matching
  // their opaque per-tenant instance id against `host.catalog` can never
  // succeed, so skip that lookup entirely rather than surfacing a false
  // "not available in the current catalogue" error for a perfectly real,
  // installed instance.
  let item = null
  if (kind && _NO_CATALOG_DOMAINS.has(domain)) {
    // The click site (InstallableRailItem) already has everything needed —
    // it forwarded its own tile state (`display`), which was itself
    // self-derived from the real instance (InstallableRail.js
    // `_deriveSelfKinds`). No network round-trip, no catalogue guess.
    item = {
      id: kind,
      kind,
      name: selfDisplay?.label || kind,
      domain,
      display: selfDisplay || undefined
    }
    const filterError = el.call('checkCatalogFilter', item, catalogFilter)
    if (filterError) {
      host.update?.({
        installModal: {
          item,
          step: 'config',
          draft: {},
          domain,
          defaultScope,
          catalogFilter,
          onInstalled,
          _catalogError: filterError
        },
        _installModalError: null
      })
      return
    }
  } else if (kind) {
    item = _findItemByKind(catalog, kind)

    if (!item) {
      // Kind not found in catalogue — open modal with catalogError.
      host.update?.({
        installModal: {
          item: { name: kind, id: kind, kind: domain || 'integration' },
          step: 'config',
          draft: {},
          domain,
          defaultScope,
          catalogFilter,
          onInstalled,
          _catalogError: el
            .call('polyglot', 'marketplace.install.kindNotInCatalogue')
            .replace('{kind}', () => kind)
        },
        _installModalError: null
      })
      return
    }

    // 4. Validate catalogFilter against the found item.
    const filterError = el.call('checkCatalogFilter', item, catalogFilter)
    if (filterError) {
      host.update?.({
        installModal: {
          item,
          step: 'config',
          draft: {},
          domain,
          defaultScope,
          catalogFilter,
          onInstalled,
          _catalogError: filterError
        },
        _installModalError: null
      })
      return
    }
  } else if (domain && _NO_CATALOG_DOMAINS.has(domain) && _NO_CATALOG_ADD_SCHEMAS[domain]) {
    // No kind AND no catalogue — the "Add <domain>" entry point (see
    // InstallableRailAddButton.onClick). There's nothing to pick from a
    // catalogue; the item IS the config form (tickets/workspace.md
    // "`_submitMcpDomain` install-modal write path is a stale stub" —
    // without a real item here, `submitInstallModal` bails out on its
    // `if (!modal?.item) return` guard before ever reaching the domain
    // dispatcher, so the write path could never even be exercised through
    // this modal).
    item = {
      id: domain,
      kind: domain,
      name: domain === 'mcp' ? el.call('polyglot', 'marketplace.install.mcpConnector') : domain,
      domain,
      _liveKind: _NO_CATALOG_ADD_SCHEMAS[domain]
    }
  }

  // 5. Build initial draft — apply defaultScope if provided.
  const draft = {}
  if (defaultScope?.type) {
    draft.scopeType = defaultScope.type
    if (defaultScope.id) draft.scopeId = defaultScope.id
  } else if (item?._liveKind?.default_scope) {
    draft.scopeType = item._liveKind.default_scope
  }

  // 6. Determine opening step.
  const openStep = defaultScope || installed ? 'config' : 'scope'

  host.update?.({
    installModal: {
      item: item || null,
      step: openStep,
      draft,
      domain,
      defaultScope,
      catalogFilter,
      onInstalled,
      _catalogError: null
    },
    _installModalError: null
  })
}
